import React, { useState } from 'react';
import { MessageCircle, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { supabase } from '../lib/supabase';

const WhatsAppActivation = () => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [hasSubscription, setHasSubscription] = useState(false);

  React.useEffect(() => {
    checkSubscription();
  }, []);

  const checkSubscription = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('subscription_status, subscription_ends_at')
        .eq('id', session.user.id)
        .single();

      if (profile?.subscription_status === 'active' && 
          new Date(profile?.subscription_ends_at) > new Date()) {
        setHasSubscription(true);
      }
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };
  
  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return numbers;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Você precisa estar logado para ativar o WhatsApp');
      }

      const formattedNumber = phoneNumber.replace(/\D/g, '');
      
      // Add Brazil country code if not present
      const fullNumber = formattedNumber.startsWith('55') 
        ? formattedNumber 
        : `55${formattedNumber}`;

      const { data, error } = await supabase.rpc(
        'activate_whatsapp_session',
        { 
          user_id: session.user.id,
          phone_number: fullNumber
        }
      );

      if (error) {
        throw error;
      }
      
      setSuccess(true);
    } catch (err) {
      setError(err.message || 'Erro ao processar solicitação. Por favor, tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="bg-green-50 p-6 rounded-lg">
        <div className="flex items-center gap-3 text-green-700 mb-4">
          <CheckCircle2 className="h-6 w-6" />
          <h3 className="font-semibold">WhatsApp Ativado!</h3>
        </div>
        <p className="text-green-600">
          Seu assistente está pronto! Você receberá uma mensagem de confirmação em instantes.
        </p>
      </div>
    );
  }

  if (!hasSubscription) {
    return (
      <div className="bg-yellow-50 p-6 rounded-lg">
        <div className="flex items-center gap-3 text-yellow-700 mb-4">
          <AlertTriangle className="h-6 w-6" />
          <h3 className="font-semibold">Assinatura Necessária</h3>
        </div>
        <p className="text-yellow-600 mb-6">
          Para ativar o assistente no WhatsApp, você precisa ter uma assinatura ativa.
        </p>
        <a
          href="https://pay.kiwify.com.br/cSvs3ux"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-block bg-yellow-600 hover:bg-yellow-700 text-white px-6 py-2 rounded-lg font-medium transition-colors"
        >
          Assinar Agora
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-lg border border-slate-100">
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <MessageCircle className="h-5 w-5 text-[#25D366]" />
        Ativar Assistente no WhatsApp
      </h3>
      
      <p className="text-slate-600 mb-6">
        Para começar a usar o assistente, confirme seu número do WhatsApp abaixo.
        Você receberá uma mensagem de confirmação em instantes.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Seu número do WhatsApp
          </label>
          <input
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
            placeholder="(11) 99999-9999"
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            maxLength={15}
            required
          />
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 p-3 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || phoneNumber.replace(/\D/g, '').length < 11}
          className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="h-5 w-5 animate-spin" />
              Ativando...
            </>
          ) : (
            <>
              <MessageCircle className="h-5 w-5" />
              Ativar WhatsApp
            </>
          )}
        </button>
      </form>

      <p className="mt-6 text-sm text-slate-500 flex items-start gap-2">
        <MessageCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
        <span>
          Seu número será usado apenas para enviar análises e notificações importantes.
          Você pode desativar o serviço a qualquer momento.
        </span>
      </p>
    </div>
  );
};

export default WhatsAppActivation;